import { normalizeCategorySlug } from "@/lib/category-slug";
import type {
  EstimatorBenchmark,
  FounderFitAttributes,
  FounderFitScore,
  NicheConfidenceBand,
  NicheInsight,
  NichePercentiles,
  NicheRanking,
  QualitativeLevel,
  ScoreExplanation,
  ScoreFactorKey,
  StartupComparison,
  StartupRecord,
  TenKEstimate
} from "@/lib/types";

const TEN_K = 10_000;

const CATEGORY_LABELS: Record<string, string> = {
  unknown: "Uncategorized",
  ai: "Artificial Intelligence",
  saas: "SaaS",
  "developer-tools": "Developer Tools",
  fintech: "Fintech",
  marketing: "Marketing",
  ecommerce: "E-commerce",
  productivity: "Productivity",
  "design-tools": "Design Tools",
  "no-code": "No-Code",
  analytics: "Analytics",
  "crypto-web3": "Crypto & Web3",
  education: "Education",
  "health-fitness": "Health & Fitness",
  "social-media": "Social Media",
  "content-creation": "Content Creation",
  sales: "Sales",
  "customer-support": "Customer Support",
  recruiting: "Recruiting",
  "real-estate": "Real Estate",
  travel: "Travel",
  legal: "Legal",
  security: "Security",
  "iot-hardware": "IoT & Hardware",
  "green-tech": "Green Tech",
  entertainment: "Entertainment",
  games: "Games",
  community: "Community",
  "news-magazines": "News & Magazines",
  utilities: "Utilities",
  marketplace: "Marketplace",
  "mobile-apps": "Mobile Apps"
};

/** Weights for the opportunity score numerator (must sum to 1) */
const OPPORTUNITY_SCORE_WEIGHTS = {
  marketSize: 0.3,
  growth: 0.25,
  demand: 0.25,
  monetization: 0.2
} as const;

export const VALID_CATEGORY_SLUGS = Object.keys(
  CATEGORY_LABELS
) as (keyof typeof CATEGORY_LABELS)[];

function average(values: number[]): number {
  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

export function median(values: number[]): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

function percentileValue(values: number[], p: number): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const index = Math.min(sorted.length - 1, Math.max(0, Math.ceil((p / 100) * sorted.length) - 1));
  return sorted[index];
}

function normalize(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return 0;
  if (min === max) return 0.5;
  return (value - min) / (max - min);
}

function confidenceMultiplier(count: number): number {
  if (count < 5) return 0.65;
  if (count < 10) return 0.8;
  if (count < 15) return 0.9;
  return 1;
}

function confidenceBand(multiplier: number): NicheConfidenceBand {
  if (multiplier >= 1) return "high";
  if (multiplier >= 0.8) return "medium";
  return "low";
}

function percentileRank(values: number[], value: number, inverse = false): number {
  if (!values.length) return 50;
  const sorted = [...values].sort((a, b) => a - b);
  const lessOrEqual = sorted.filter((entry) => entry <= value).length;
  const rank = (lessOrEqual / sorted.length) * 100;
  return Number((inverse ? 100 - rank : rank).toFixed(1));
}

function percentilesPlaceholder(): NichePercentiles {
  return {
    opportunityScore: 50,
    medianMrr: 50,
    totalMrr: 50,
    averageGrowth30d: 50,
    startupCount: 50,
    forSaleRatio: 50,
    top3MrrShare: 50,
    marketDemand: 50,
    avgRevenuePerVisitor: 50
  };
}

function explanationPlaceholder(): ScoreExplanation {
  return {
    strongestFactor: "totalMrr",
    weakestFactor: "avgRevenuePerVisitor",
    summary: "This niche needs more data to fully explain its score."
  };
}

function factorLabel(key: ScoreFactorKey): string {
  switch (key) {
    case "totalMrr":
      return "market size";
    case "averageGrowth30d":
      return "growth";
    case "marketDemand":
      return "demand";
    case "avgRevenuePerVisitor":
      return "monetization";
    case "forSaleRatio":
      return "sale liquidity";
    default:
      return key;
  }
}

export { normalizeCategorySlug } from "@/lib/category-slug";

export function categoryLabel(slug: string): string {
  const normalized = normalizeCategorySlug(slug);
  return CATEGORY_LABELS[normalized] ?? normalized.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function isValidCategory(slug: string): boolean {
  const normalized = normalizeCategorySlug(slug);
  return normalized in CATEGORY_LABELS;
}

export function computeMetricPercentiles(entries: NicheRanking[]): NicheRanking[] {
  const opportunityScores = entries.map((entry) => entry.opportunityScore);
  const medianMrrValues = entries.map((entry) => entry.medianMrr);
  const totalMrrValues = entries.map((entry) => entry.totalMrr);
  const averageGrowthValues = entries.map((entry) => entry.averageGrowth30d ?? 0);
  const startupCountValues = entries.map((entry) => entry.startupCount);
  const forSaleRatioValues = entries.map((entry) => entry.forSaleRatio);
  const top3ShareValues = entries.map((entry) => entry.top3MrrShare);
  const marketDemandValues = entries.map((entry) => entry.marketDemand);
  const avgRevenuePerVisitorValues = entries.map((entry) => entry.avgRevenuePerVisitor);

  return entries.map((entry) => ({
    ...entry,
    percentiles: {
      opportunityScore: percentileRank(opportunityScores, entry.opportunityScore),
      medianMrr: percentileRank(medianMrrValues, entry.medianMrr),
      totalMrr: percentileRank(totalMrrValues, entry.totalMrr),
      averageGrowth30d: percentileRank(averageGrowthValues, entry.averageGrowth30d ?? 0),
      startupCount: percentileRank(startupCountValues, entry.startupCount, true),
      forSaleRatio: percentileRank(forSaleRatioValues, entry.forSaleRatio),
      top3MrrShare: percentileRank(top3ShareValues, entry.top3MrrShare, true),
      marketDemand: percentileRank(marketDemandValues, entry.marketDemand),
      avgRevenuePerVisitor: percentileRank(avgRevenuePerVisitorValues, entry.avgRevenuePerVisitor)
    }
  }));
}

const SCORE_FACTOR_WEIGHTS: Record<Exclude<ScoreFactorKey, "forSaleRatio">, number> = {
  totalMrr: OPPORTUNITY_SCORE_WEIGHTS.marketSize,
  averageGrowth30d: OPPORTUNITY_SCORE_WEIGHTS.growth,
  marketDemand: OPPORTUNITY_SCORE_WEIGHTS.demand,
  avgRevenuePerVisitor: OPPORTUNITY_SCORE_WEIGHTS.monetization
};

export function buildScoreExplanation(entry: NicheRanking): ScoreExplanation {
  const weightedEntries: Array<[ScoreFactorKey, number]> = (
    ["totalMrr", "averageGrowth30d", "marketDemand", "avgRevenuePerVisitor"] as const
  ).map((key) => [key, entry.scoreBreakdown[key] * SCORE_FACTOR_WEIGHTS[key]]);

  weightedEntries.sort((a, b) => b[1] - a[1]);
  const strongestFactor = weightedEntries[0]?.[0] ?? "totalMrr";
  const weakestFactor = weightedEntries[weightedEntries.length - 1]?.[0] ?? "avgRevenuePerVisitor";
  const summary = `This niche is carried most by ${factorLabel(strongestFactor)} and held back most by ${factorLabel(weakestFactor)}.`;

  return {
    strongestFactor,
    weakestFactor,
    summary
  };
}

export function buildNicheInsights(entry: NicheRanking): NicheInsight[] {
  const candidates: Array<NicheInsight & { weight: number }> = [];

  if (entry.scoreBreakdown.totalMrr >= 0.7) {
    candidates.push({
      id: "market-size",
      text: "Total verified MRR is strong enough to support a large market thesis.",
      tone: "positive",
      weight: 10
    });
  }

  if (entry.scoreBreakdown.averageGrowth30d >= 0.65 && (entry.averageGrowth30d ?? 0) > 0) {
    candidates.push({
      id: "growth",
      text: "Fast revenue growth signals a niche with recent momentum.",
      tone: "positive",
      weight: 9
    });
  }

  if (entry.scoreBreakdown.marketDemand >= 0.7 && entry.marketDemand > 0) {
    candidates.push({
      id: "demand",
      text: "Strong search and traffic demand indicates real market interest.",
      tone: "positive",
      weight: 8
    });
  }

  if (entry.scoreBreakdown.avgRevenuePerVisitor >= 0.65 && entry.avgRevenuePerVisitor > 0) {
    candidates.push({
      id: "monetization",
      text: "Above-average revenue per visitor suggests efficient monetization.",
      tone: "positive",
      weight: 8
    });
  }

  if (entry.startupCount < 10 && entry.totalMrr > 0) {
    candidates.push({
      id: "competition",
      text: "Competition looks lighter than average for the revenue on display.",
      tone: "positive",
      weight: 7
    });
  }

  if (entry.marketSaturation != null && entry.marketSaturation < 0.05) {
    candidates.push({
      id: "saturation",
      text: "Low market saturation — few startups per unit of demand suggests room for new entrants.",
      tone: "positive",
      weight: 8
    });
  }

  if (entry.gapScore != null && entry.gapScore > 100) {
    candidates.push({
      id: "gap",
      text: "High gap score — real demand, few competitors, and strong monetization signal an underserved market.",
      tone: "positive",
      weight: 9
    });
  }

  if (entry.forSaleRatio >= 0.18) {
    candidates.push({
      id: "liquidity",
      text: "A healthy for-sale ratio suggests an acquisition-aware market.",
      tone: "neutral",
      weight: 7
    });
  }

  if (entry.top3MrrShare >= 0.68) {
    candidates.push({
      id: "concentration",
      text: "Revenue is concentrated in a few players, so entry risk is higher.",
      tone: "warning",
      weight: 8
    });
  }

  if (entry.confidenceBand === "low") {
    candidates.push({
      id: "confidence",
      text: "Sample size is thin, so treat this niche as directional rather than definitive.",
      tone: "warning",
      weight: 7
    });
  }

  const fallbacks: Array<NicheInsight & { weight: number }> = [
    {
      id: "baseline-mrr",
      text: "Median revenue is respectable, which reduces the chance of a fake market.",
      tone: "neutral",
      weight: 3
    },
    {
      id: "baseline-growth",
      text: "Growth is mixed, so distribution matters more than a single outlier startup.",
      tone: "neutral",
      weight: 2
    },
    {
      id: "baseline-fit",
      text: "This niche needs closer startup-level inspection before you commit to it.",
      tone: "neutral",
      weight: 1
    }
  ];

  const picked: NicheInsight[] = [];
  for (const insight of [...candidates, ...fallbacks].sort((a, b) => b.weight - a.weight)) {
    if (picked.some((item) => item.id === insight.id)) continue;
    picked.push({ id: insight.id, text: insight.text, tone: insight.tone });
    if (picked.length === 3) break;
  }

  return picked;
}

const TECHNICAL_COMPLEXITY_HIGH = new Set([
  "ai", "developer-tools", "crypto-web3", "security", "iot-hardware"
]);
const TECHNICAL_COMPLEXITY_LOW = new Set([
  "community", "content-creation", "news-magazines", "utilities", "marketplace"
]);

export function buildFounderFitAttributes(niche: NicheRanking): FounderFitAttributes {
  const slug = normalizeCategorySlug(niche.category);

  let technicalComplexity: QualitativeLevel = "moderate";
  if (TECHNICAL_COMPLEXITY_HIGH.has(slug)) technicalComplexity = "high";
  else if (TECHNICAL_COMPLEXITY_LOW.has(slug)) technicalComplexity = "low";

  let marketingDifficulty: QualitativeLevel = "moderate";
  if (niche.marketSaturation != null) {
    if (niche.marketSaturation > 0.15) marketingDifficulty = "high";
    else if (niche.marketSaturation < 0.03) marketingDifficulty = "low";
  }

  let competitionIntensity: QualitativeLevel = "moderate";
  const startupCount = niche.startupCount;
  const top3Share = niche.top3MrrShare;
  if (startupCount >= 25 || top3Share >= 0.65) competitionIntensity = "high";
  else if (startupCount <= 8 && top3Share <= 0.5) competitionIntensity = "low";

  let enterpriseVsSmb: QualitativeLevel = "moderate";
  if (niche.medianMrr >= 5000) enterpriseVsSmb = "high";
  else if (niche.medianMrr <= 1500) enterpriseVsSmb = "low";

  return {
    technicalComplexity,
    marketingDifficulty,
    competitionIntensity,
    enterpriseVsSmb
  };
}

export function scoreFounderFit(niche: NicheRanking): FounderFitScore {
  const lowCompetitionScore = niche.scoreBreakdown.competitionFactor > 0
    ? Math.max(0, 1 - niche.scoreBreakdown.competitionFactor / 4)
    : 0.5;

  const score = Math.round(
    niche.scoreBreakdown.totalMrr * 35 +
    lowCompetitionScore * 30 +
    (1 - niche.top3MrrShare) * 20 +
    (niche.averageGrowth30d && niche.averageGrowth30d > 0 ? 15 : 5)
  );

  let summary = "Balanced niche with decent room for a solo founder.";
  if (score >= 75) {
    summary = "This niche looks friendly for a founder who wants real revenue without extreme crowding.";
  } else if (score < 50) {
    summary = "This niche likely needs stronger distribution or a sharper wedge to break through.";
  }

  return { score, summary };
}

export function computeNicheRankingsV2(
  startupsByCategory: Record<string, StartupRecord[]>
): NicheRanking[] {
  const baseEntries = Object.entries(startupsByCategory)
    .filter(([, startups]) => startups.length > 0)
    .map(([category, group]) => {
      const mrrValues = group.map((startup) => startup.mrr).filter((value) => value > 0);
      const growthValues = group
        .map((startup) => startup.growth_30d)
        .filter((value): value is number => value !== null && Number.isFinite(value));
      const startupCount = group.length;
      const totalMrr = group.reduce((sum, startup) => sum + startup.mrr, 0);
      const totalRevenue30d = group.reduce((sum, startup) => sum + startup.revenue_30d, 0);
      const averageMrr = startupCount > 0 ? totalMrr / startupCount : 0;
      const medianMrr = median(mrrValues);
      const averageGrowth30d = growthValues.length > 0 ? average(growthValues) : null;
      const startupsForSale = group.filter((startup) => startup.on_sale).length;
      const forSaleRatio = startupCount > 0 ? startupsForSale / startupCount : 0;
      const top3MrrShare = totalMrr > 0
        ? [...group]
            .sort((a, b) => b.mrr - a.mrr)
            .slice(0, 3)
            .reduce((sum, startup) => sum + startup.mrr, 0) / totalMrr
        : 0;
      const multiplier = confidenceMultiplier(startupCount);

      const marketDemand = group.reduce((sum, startup) => {
        const visitors = startup.visitors_last_30_days ?? 0;
        const impressions = startup.google_search_impressions_last_30_days ?? 0;
        return sum + visitors + impressions;
      }, 0);

      const revenuePerVisitorValues = group
        .map((startup) => startup.revenue_per_visitor)
        .filter((value): value is number => value !== null && Number.isFinite(value) && value > 0);
      const avgRevenuePerVisitor =
        revenuePerVisitorValues.length > 0 ? average(revenuePerVisitorValues) : 0;

      const competitionFactor = Math.log(startupCount + 1);
      const marketSaturation = marketDemand > 0 ? startupCount / marketDemand : null;
      const gapScore =
        startupCount > 0 && marketDemand > 0 && avgRevenuePerVisitor > 0
          ? (marketDemand * avgRevenuePerVisitor) / startupCount
          : null;

      return {
        category,
        label: categoryLabel(category),
        startupCount,
        totalMrr,
        medianMrr,
        averageMrr,
        marketDemand,
        avgRevenuePerVisitor,
        marketSaturation,
        gapScore,
        opportunityScore: 0,
        totalRevenue30d,
        averageGrowth: averageGrowth30d,
        averageGrowth30d,
        startupsForSale,
        forSaleRatio,
        top3MrrShare,
        confidenceBand: confidenceBand(multiplier),
        confidenceMultiplier: multiplier,
        topStartup: [...group].sort((a, b) => b.mrr - a.mrr)[0]?.name ?? "-",
        insights: [],
        scoreBreakdown: {
          totalMrr: 0,
          averageGrowth30d: 0,
          marketDemand: 0,
          avgRevenuePerVisitor: 0,
          forSaleRatio: 0,
          competitionFactor
        },
        percentiles: percentilesPlaceholder(),
        scoreExplanation: explanationPlaceholder(),
        delta: null,
        founderFitScore: null,
        acquisitionFitScore: null
      } satisfies NicheRanking;
    });

  if (!baseEntries.length) return [];

  const growthRange = baseEntries.map((entry) => entry.averageGrowth30d ?? 0);
  const totalMrrRange = baseEntries.map((entry) => entry.totalMrr);
  const marketDemandRange = baseEntries.map((entry) => entry.marketDemand);
  const avgRevenuePerVisitorRange = baseEntries.map((entry) => entry.avgRevenuePerVisitor);
  const forSaleRatioRange = baseEntries.map((entry) => entry.forSaleRatio);

  const minGrowth = Math.min(...growthRange);
  const maxGrowth = Math.max(...growthRange);
  const minTotalMrr = Math.min(...totalMrrRange);
  const maxTotalMrr = Math.max(...totalMrrRange);
  const minMarketDemand = Math.min(...marketDemandRange);
  const maxMarketDemand = Math.max(...marketDemandRange);
  const minAvgRevenuePerVisitor = Math.min(...avgRevenuePerVisitorRange);
  const maxAvgRevenuePerVisitor = Math.max(...avgRevenuePerVisitorRange);
  const minForSaleRatio = Math.min(...forSaleRatioRange);
  const maxForSaleRatio = Math.max(...forSaleRatioRange);

  const scored = baseEntries.map((entry) => {
    const normalizedTotalMrr = normalize(entry.totalMrr, minTotalMrr, maxTotalMrr);
    const normalizedGrowth = normalize(entry.averageGrowth30d ?? 0, minGrowth, maxGrowth);
    const normalizedDemand =
      maxMarketDemand > minMarketDemand
        ? normalize(entry.marketDemand, minMarketDemand, maxMarketDemand)
        : entry.marketDemand > 0 ? 1 : 0;
    const normalizedRevenuePerVisitor =
      maxAvgRevenuePerVisitor > minAvgRevenuePerVisitor
        ? normalize(entry.avgRevenuePerVisitor, minAvgRevenuePerVisitor, maxAvgRevenuePerVisitor)
        : entry.avgRevenuePerVisitor > 0 ? 1 : 0;
    const forSaleRatioScore = normalize(entry.forSaleRatio, minForSaleRatio, maxForSaleRatio);

    const numerator =
      normalizedTotalMrr * OPPORTUNITY_SCORE_WEIGHTS.marketSize +
      normalizedGrowth * OPPORTUNITY_SCORE_WEIGHTS.growth +
      normalizedDemand * OPPORTUNITY_SCORE_WEIGHTS.demand +
      normalizedRevenuePerVisitor * OPPORTUNITY_SCORE_WEIGHTS.monetization;

    const competitionFactor = entry.scoreBreakdown.competitionFactor;
    const rawScore = competitionFactor > 0 ? numerator / competitionFactor : numerator;

    return {
      ...entry,
      opportunityScore: Math.round(rawScore * 100 * entry.confidenceMultiplier),
      scoreBreakdown: {
        totalMrr: Number(normalizedTotalMrr.toFixed(3)),
        averageGrowth30d: Number(normalizedGrowth.toFixed(3)),
        marketDemand: Number(normalizedDemand.toFixed(3)),
        avgRevenuePerVisitor: Number(normalizedRevenuePerVisitor.toFixed(3)),
        forSaleRatio: Number(forSaleRatioScore.toFixed(3)),
        competitionFactor: Number(competitionFactor.toFixed(3))
      }
    };
  });

  const withPercentiles = computeMetricPercentiles(scored);

  return withPercentiles
    .map((entry) => {
      const scoreExplanation = buildScoreExplanation(entry);
      const founderFit = scoreFounderFit(entry);
      const acquisitionFit = Math.round(
        entry.scoreBreakdown.forSaleRatio * 40 +
        entry.scoreBreakdown.totalMrr * 25 +
        (1 - entry.top3MrrShare) * 20 +
        (entry.averageGrowth30d && entry.averageGrowth30d > 0 ? 15 : 8)
      );

      const enriched: NicheRanking = {
        ...entry,
        scoreExplanation,
        founderFitScore: founderFit.score,
        acquisitionFitScore: acquisitionFit,
        founderFitAttributes: buildFounderFitAttributes(entry),
        insights: []
      };

      enriched.insights = buildNicheInsights(enriched);
      return enriched;
    })
    .sort((a, b) => {
      if (b.opportunityScore !== a.opportunityScore) {
        return b.opportunityScore - a.opportunityScore;
      }
      return b.medianMrr - a.medianMrr;
    });
}

export function applyLeaderboardDeltas(
  entries: NicheRanking[],
  previousScores: Record<string, number | null>,
  days: number
): NicheRanking[] {
  return entries.map((entry) => {
    const previousScore = previousScores[entry.category] ?? null;
    const delta = previousScore === null ? null : computeScoreDelta(entry.opportunityScore, previousScore);

    return {
      ...entry,
      delta: {
        previousScore,
        delta,
        days,
        direction: delta === null ? "unknown" : delta > 0 ? "up" : delta < 0 ? "down" : "flat"
      }
    };
  });
}

export function computeNicheRankings(startups: StartupRecord[]): NicheRanking[] {
  const buckets = new Map<string, StartupRecord[]>();

  for (const startup of startups) {
    const group = buckets.get(startup.category) ?? [];
    group.push(startup);
    buckets.set(startup.category, group);
  }

  return computeNicheRankingsV2(Object.fromEntries(buckets));
}

export function getStartupsForCategory(
  startups: StartupRecord[],
  category: string
): StartupRecord[] {
  return startups
    .filter((startup) => startup.category === category)
    .sort((a, b) => b.mrr - a.mrr);
}

export function buildEstimatorBenchmark(startups: StartupRecord[]): EstimatorBenchmark {
  const mrrValues = startups.map((startup) => startup.mrr).filter((value) => value > 0);
  const arpuValues = startups
    .map((startup) => {
      const denominator = startup.active_subscriptions > 0 ? startup.active_subscriptions : startup.customers;
      return denominator > 0 ? startup.mrr / denominator : null;
    })
    .filter((value): value is number => value !== null && value > 0);

  const above10k = startups.filter((startup) => startup.mrr >= TEN_K && startup.founded_date);

  const monthsToReach = above10k
    .map((startup) => {
      if (!startup.founded_date) return null;
      const founded = new Date(startup.founded_date);
      const months = (Date.now() - founded.getTime()) / (1000 * 60 * 60 * 24 * 30.44);
      return months > 0 ? months : null;
    })
    .filter((value): value is number => value !== null);

  const growthPerMonth = startups
    .filter((startup) => startup.founded_date && startup.mrr > 0)
    .map((startup) => {
      if (!startup.founded_date) return null;
      const founded = new Date(startup.founded_date);
      const months = (Date.now() - founded.getTime()) / (1000 * 60 * 60 * 24 * 30.44);
      return months > 0 ? startup.mrr / months : null;
    })
    .filter((value): value is number => value !== null && Number.isFinite(value));

  return {
    marketMedianGrowthPerMonth: Number(median(growthPerMonth).toFixed(2)),
    marketMedianMonthsTo10k: Number(median(monthsToReach).toFixed(1)),
    nicheMedianMrr: Number(median(mrrValues).toFixed(0)),
    nicheTopQuartileMrr: Number(percentileValue(mrrValues, 75).toFixed(0)),
    nicheMedianArpu: Number(median(arpuValues).toFixed(2)),
    sampleSize: startups.length
  };
}

export function estimateTimeTo10k(
  currentMrr: number,
  startupAgeMonths: number,
  benchmark: EstimatorBenchmark,
  arpu?: number
): TenKEstimate | null {
  if (currentMrr <= 0 || startupAgeMonths <= 0) return null;

  const userGrowthPerMonth = currentMrr / startupAgeMonths;
  const blendedGrowth = userGrowthPerMonth * 0.65 + benchmark.marketMedianGrowthPerMonth * 0.35;

  if (blendedGrowth <= 0) return null;

  const remaining = Math.max(0, (TEN_K - currentMrr) / blendedGrowth);
  const total = startupAgeMonths + remaining;
  const monthsToMedian = Math.max(0, (benchmark.nicheMedianMrr - currentMrr) / blendedGrowth);
  const monthsToTopQuartile = Math.max(0, (benchmark.nicheTopQuartileMrr - currentMrr) / blendedGrowth);
  const effectiveArpu = arpu && arpu > 0 ? arpu : benchmark.nicheMedianArpu > 0 ? benchmark.nicheMedianArpu : null;

  const customersNeededForMedian = effectiveArpu
    ? Math.ceil(benchmark.nicheMedianMrr / effectiveArpu)
    : null;
  const customersNeededForTopQuartile = effectiveArpu
    ? Math.ceil(benchmark.nicheTopQuartileMrr / effectiveArpu)
    : null;

  let currentPercentile = 50;
  if (benchmark.nicheMedianMrr <= 0) {
    currentPercentile = 50;
  } else if (currentMrr < benchmark.nicheMedianMrr) {
    currentPercentile = Math.max(5, Math.min(49, (currentMrr / benchmark.nicheMedianMrr) * 50));
  } else if (benchmark.nicheTopQuartileMrr > benchmark.nicheMedianMrr && currentMrr < benchmark.nicheTopQuartileMrr) {
    currentPercentile = 50 + (
      (currentMrr - benchmark.nicheMedianMrr) /
      (benchmark.nicheTopQuartileMrr - benchmark.nicheMedianMrr)
    ) * 25;
  } else {
    currentPercentile = Math.min(95, 75 + ((currentMrr - benchmark.nicheTopQuartileMrr) / Math.max(benchmark.nicheTopQuartileMrr, 1)) * 15);
  }

  let confidence: "low" | "medium" | "high" = "medium";
  if (benchmark.sampleSize < 8 || remaining > 12) confidence = "low";
  if (benchmark.sampleSize >= 15 && remaining <= 6) confidence = "high";

  const target = new Date();
  target.setMonth(target.getMonth() + Math.round(remaining));

  return {
    estimatedMonthsRemaining: Number(remaining.toFixed(1)),
    estimatedTotalMonths: Number(total.toFixed(1)),
    targetDateIso: target.toISOString().slice(0, 10),
    confidence,
    currentPercentile: Number(currentPercentile.toFixed(1)),
    medianTargetMrr: benchmark.nicheMedianMrr,
    topQuartileTargetMrr: benchmark.nicheTopQuartileMrr,
    monthsToMedian: Number(monthsToMedian.toFixed(1)),
    monthsToTopQuartile: Number(monthsToTopQuartile.toFixed(1)),
    customersNeededForMedian,
    customersNeededForTopQuartile,
    sampleSizeWarning: benchmark.sampleSize < 8
      ? "This niche has a small sample, so treat the estimate as directional."
      : null
  };
}

export function compareStartupToNiche(
  startup: StartupRecord,
  benchmark: EstimatorBenchmark,
  niche: NicheRanking
): StartupComparison {
  const mrrVsMedian = benchmark.nicheMedianMrr > 0 ? startup.mrr / benchmark.nicheMedianMrr : 1;
  const growthVsAverage = startup.growth_30d !== null && niche.averageGrowth30d !== null
    ? startup.growth_30d - niche.averageGrowth30d
    : null;
  const typicalCustomerBase = benchmark.nicheMedianArpu > 0 ? Math.round(benchmark.nicheMedianMrr / benchmark.nicheMedianArpu) : null;
  const currentCustomerBase = startup.active_subscriptions > 0 ? startup.active_subscriptions : startup.customers;
  const customersVsTypical = typicalCustomerBase && currentCustomerBase > 0
    ? currentCustomerBase / typicalCustomerBase
    : null;

  let representation: StartupComparison["representation"] = "typical";
  if (mrrVsMedian >= 2.5) {
    representation = "outlier";
  } else if (mrrVsMedian >= 1.3) {
    representation = "strong";
  } else if (mrrVsMedian < 0.5) {
    representation = "early";
  }

  const summary = representation === "outlier"
    ? "This startup is far above the typical niche benchmark."
    : representation === "strong"
      ? "This startup is ahead of the niche median, but still comparable."
      : representation === "early"
        ? "This startup is earlier than the niche median benchmark."
        : "This startup looks representative of the current niche pack.";

  return {
    mrrVsMedian: Number(mrrVsMedian.toFixed(2)),
    growthVsAverage: growthVsAverage !== null ? Number(growthVsAverage.toFixed(1)) : null,
    customersVsTypical: customersVsTypical !== null ? Number(customersVsTypical.toFixed(2)) : null,
    isOnSaleComparedToMarket: startup.on_sale && niche.forSaleRatio < 0.15
      ? "more-liquid"
      : !startup.on_sale && niche.forSaleRatio >= 0.15
        ? "less-liquid"
        : "neutral",
    representation,
    summary
  };
}

export function computeScoreDelta(current: number, previous: number): number {
  return Number((current - previous).toFixed(1));
}
